import { Stack } from '@mui/material';
import { memo, useMemo } from 'react';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { PartnerTab } from 'src/components/SidePanelTabs/PartnerTab/partner-tab';
import { Conversation, useGroupRules, useResolvedAddress, UserGroupRules } from 'src/graphql';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { ConversationSiteCard } from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/conversation-site-card';
import { ConversationSidePanelProvider } from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/conversation-side-panel-provider';
import { conversationSidePanelSurface } from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/conversation-surface';
import { useGlobalStore } from 'src/store';
import { ConversationCard } from './conversation-card';
import { PartnerCard } from './partner-card';
import { SidePanelToolsSlotProvider } from './side-panel-tools-slot';

// Clearance for the floating personalize tool that the column pins above the cards.
const FLOATING_TOOLS_CLEARANCE = '2.75rem';

interface ConversationSidePanelProps {
  conversation: Conversation;
  onUpdate: (c: Conversation) => Promise<void>;
  /** Sticky node in the scrolling column that hosts the floating panel tools. */
  toolsSlot?: HTMLElement | null;
}

const ConversationSidePanelComponent = ({
  conversation,
  onUpdate,
  toolsSlot,
}: ConversationSidePanelProps) => {
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { groupRules, checkValidity } = useGroupRules();
  const canPersonalize = checkValidity([UserGroupRules.AccessToPersonalizeSidepanel]);
  const canAccessToPartnerTab = !!groupRules[UserGroupRules.AccessToPartnerTab];
  const canSeePartnersTab =
    canAccessToPartnerTab || (isMultiTenancyMode && selectedPartnerId === null);

  const { resolvedAddress, loading } = useResolvedAddress(conversation.address);

  const conversationWithResolvedAddress = useMemo(
    () =>
      resolvedAddress && conversation.address !== resolvedAddress
        ? { ...conversation, address: resolvedAddress }
        : conversation,
    [conversation, resolvedAddress],
  );

  const isResolvingAddress = loading && !!conversation.address;

  return (
    <BlockContainer
      // the cards inside carry the only frame, so the column itself stays bare
      border="none !important"
      sx={{
        height: 'auto',
        // the collapsing wrapper sets nowrap for its width animation; undo it here or the content outgrows the column
        whiteSpace: 'normal',
      }}
    >
      <SidePanelToolsSlotProvider value={toolsSlot ?? null}>
        <CrossPartnerUserSettingsProvider>
          <ConversationSidePanelProvider
            key={`properties-${conversation.conversationId}`}
            conversation={conversationWithResolvedAddress}
            onUpdate={onUpdate}
          >
            {/* the personalization layout is shared by the summary accordion, the
                properties panel, and the site/client cards in the stack; it needs
                the custom-attributes context, so it cannot sit higher */}
            <SidePanelPersonalizationProvider surface={conversationSidePanelSurface}>
              <Stack
                data-testid="conversation-side-panel-cards"
                gap={1}
                sx={(theme) => ({
                  // no left inset on desktop: the resize handle already provides that gap
                  padding: '0.5rem 0.5rem 0.5rem 0',
                  // clear the floating tool so the first card starts below it, then scrolls under
                  ...(canPersonalize && { paddingTop: FLOATING_TOOLS_CLEARANCE }),
                  [theme.breakpoints.down('sm')]: { paddingLeft: '0.5rem' },
                })}
              >
                {canSeePartnersTab && conversation.partner ? (
                  <PartnerCard
                    partner={conversation.partner}
                    data-testid="conversation-partner-card"
                  >
                    <PartnerTab conversation={conversation} />
                  </PartnerCard>
                ) : null}
                <ConversationCard
                  conversation={conversationWithResolvedAddress}
                  loading={isResolvingAddress}
                />
                {!isResolvingAddress && (
                  <ConversationSiteCard
                    key={`site-${conversationWithResolvedAddress.conversationId}`}
                    conversation={conversationWithResolvedAddress}
                  />
                )}
              </Stack>
            </SidePanelPersonalizationProvider>
          </ConversationSidePanelProvider>
        </CrossPartnerUserSettingsProvider>
      </SidePanelToolsSlotProvider>
    </BlockContainer>
  );
};

export const ConversationSidePanel = memo(ConversationSidePanelComponent);
